import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useState, useRef } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import { apiGetFareCards, apiUnlockPhysicalFarecard } from '../services/api';
import NoFarecards from '../usecases/farecard/NoFarecards';
import FarecardList from '../usecases/farecard/FarecardList';
import FarecardAdd from '../usecases/farecard/FarecardAdd';
import AppModal from '../components/AppModal';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import { Farecard } from '../services/types/apitypes';
import { cardsAllowCreateVirtual, cardsIsVisbleCard } from '../utils/cards';
import { useHistory } from 'react-router';
import { storageSetCards } from '../utils/storage';
import AppNavBar from '../components/AppNavBar';
import { LoadingController } from '../utils/LoadingController';
import { useLoading } from '../context/LoadingContext';
import { useToaster } from '../hooks/useToaster';

const CardsPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const router = useHistory();
  const [t] = useTranslation('global');
  const [present] = useToaster();

  const { isLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [cards, setCards] = useState<Farecard[] | undefined>(undefined);

  const [isAdding, setIsAdding] = useState<boolean>(false);

  const cardsRef = useRef<any>([]);

  const loadDataFromApi = async () => {
    LoadingController.show();
    try {
      const response = await apiGetFareCards(auth);
      storageSetCards(response.data);
      setCards(response.data);
    } catch (e) {
      console.log('Error occurred while retrieving data', e);
    } finally {
      LoadingController.hide();
    }
  };

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    loadDataFromApi();
  }, [auth.isLoading]);

  const refresh = (e: CustomEvent) => {
    loadDataFromApi().then(() => e.detail.complete());
    cardsRef.current?.forEach((cardRef: any) => {
      cardRef?.current?.fetchCappingPots();
    });
  };

  const addValue = (f: Farecard) => {
    hideDialogs();
    router.push('/card/' + f.cardId + '/value');
  };

  const showOptions = (f: Farecard) => {
    router.push('/card/' + f.cardId + '/options');
  };

  const hideDialogs = () => {
    setIsAdding(false);
  };

  const goToAddCard = () => {
    hideDialogs();
    setIsAdding(true);
  };

  const addVirtual = () => {
    hideDialogs();
    router.push('/cardsaddvirtual');
  };

  const addPhysical = () => {
    hideDialogs();
    router.push('/cardsaddphysical');
  };

  const allowVirtual = () => {
    return cardsAllowCreateVirtual(cards);
  };

  const unlockCardFinish = async (card: Farecard) => {
    if (isSubmitting) {
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiUnlockPhysicalFarecard(card.cardId!, auth);
      await loadDataFromApi().then(() => console.log('Loaded cards again'));
      present(getAppSuccess(t('farecards.unlocksuccess')));
    } catch (error) {
      console.error('Error unlocking card ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('farecards.unlocknosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const isOneCardVisible = () => {
    if (cards && cards.length) {
      for (const card of cards) {
        if (cardsIsVisbleCard(card)) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <IonPage id="farecard-page">
      <>
        <AppPageHeader
          isPageActive={isPageActive}
          back={() => returnToMain(router)}
          title={t('farecards.title')}
        ></AppPageHeader>
        <IonContent fullscreen className="ion-padding default-content">
          <IonRefresher slot="fixed" onIonRefresh={refresh}>
            <IonRefresherContent></IonRefresherContent>
          </IonRefresher>
          {process.env.REACT_APP_URPASS_AVAILABLE === 'true' && (
            <AppNavBar
              items={[
                { label: t('home.mycards'), url: 'cards' },
                { label: t('home.agencytickets'), url: 'urpass' },
              ]}
              selected={0}
            />
          )}
          {isOneCardVisible() ? (
            <FarecardList
              goToAdd={goToAddCard}
              unlock={unlockCardFinish}
              addValue={addValue}
              showOptions={showOptions}
              cards={cards}
              itemsRef={cardsRef}
            />
          ) : (
            !isLoading && <NoFarecards goToAdd={goToAddCard}></NoFarecards>
          )}
          <AppModal isOpen={isAdding} close={() => setIsAdding(false)}>
            <FarecardAdd
              allowVirtual={allowVirtual()}
              addVirtual={() => addVirtual()}
              addPhysical={() => addPhysical()}
              goBack={() => setIsAdding(false)}
            ></FarecardAdd>
          </AppModal>
        </IonContent>
      </>
    </IonPage>
  );
};

export default CardsPage;
